import React from "react";
import styles from "./recommendedProducts.module.css";
import ProductImage from "../../../../components/common/ProductImage/ProductImage";
import { Add as AddIcon } from "@mui/icons-material";

interface RecommendedProduct {
  id: string | number;
  name: string;
  url: string;
  images: {
    small?: string | null;
    medium?: string | null;
    thumbnail?: string | null;
  }[];
  price?: {
    regPrice?: number;
    discount?: { newPrice: number; endDatetime?: string } | null;
    priceRange?: { min: number; max: number } | null;
  };
  type?: string;
  hasChildren?: boolean;
  stock?: number;
  customizableOptions?: any[];
  sku?: string;
  partner?: { name?: string };
  attributes?: any[];
  logistics?: any;
  expressDelivery?: boolean;
  fastestDeliveryDate?: string; // Add this if you want to show delivery info
}

interface Props {
  products: RecommendedProduct[];
  title?: string;
}

const getColorSwatches = (attributes: any[] = []) => {
  const colorAttr = attributes.find(
    (attr: any) =>
      attr.name?.toLowerCase() === "color" ||
      attr.uniqueName?.toLowerCase() === "color"
  );
  if (!colorAttr || !Array.isArray(colorAttr.values)) return [];
  return colorAttr.values
    .map((v: any) => v.colorHex)
    .filter((hex: string | null | undefined) => !!hex);
};

const getPriceInfo = (product: RecommendedProduct) => {
  let centralPrice: string | null = null;
  let oldPrice: string | null = null;
  let priceRange: string | null = null;

  if (product.type === "simple") {
    if (product.price?.discount?.newPrice) {
      centralPrice = `Ahora S/ ${Number(
        product.price.discount.newPrice
      ).toFixed(2)}`;
      oldPrice = `S/ ${Number(product.price.regPrice).toFixed(2)}`;
    } else if (product.price?.regPrice) {
      centralPrice = `S/ ${Number(product.price.regPrice).toFixed(2)}`;
    }
  } else {
    if (product.price?.discount?.newPrice) {
      centralPrice = `Ahora S/ ${Number(
        product.price.discount.newPrice
      ).toFixed(2)}`;
      oldPrice = `S/ ${Number(product.price.regPrice).toFixed(2)}`;
    } else if (product.price?.priceRange) {
      centralPrice = `Desde S/ ${Number(product.price.priceRange.min).toFixed(
        2
      )}`;
      if (product.price.priceRange.min !== product.price.priceRange.max) {
        priceRange = `Opciones entre S/ ${Number(
          product.price.priceRange.min
        ).toFixed(2)} - S/ ${Number(product.price.priceRange.max).toFixed(2)}`;
      }
    } else if (product.price?.regPrice) {
      centralPrice = `S/ ${Number(product.price.regPrice).toFixed(2)}`;
    }
  }

  return { centralPrice, oldPrice, priceRange };
};

const RecommendedProducts: React.FC<Props> = ({
  products,
  title = "Tal vez buscas...",
}) => {
  if (!products || products.length === 0) return null;

  return (
    <section
      aria-label={`${title} productos recomendados`}
      style={{ marginTop: 48 }}
    >
      <h2 style={{ fontSize: "1.5rem", fontWeight: 700, marginBottom: 16 }}>
        {title}
      </h2>
      <div className={styles.slider}>
        {products.map((product) => {
          const colors = getColorSwatches(product.attributes);
          const { centralPrice, oldPrice, priceRange } = getPriceInfo(product);
          return (
            <div key={product.id} className={styles.card}>
              <a
                href={`/p/${product.url}`}
                className={styles.productLink}
                title={`Ver detalles de ${product.name}`}
                aria-label={`Ver detalles de ${product.name}`}
              >
                <div
                  className={styles.imageContainer}
                  style={{ position: "relative" }}
                >
                  {product.expressDelivery && (
                    <span
                      style={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        background: "#ff9800",
                        color: "#fff",
                        fontSize: 11,
                        fontWeight: 600,
                        borderRadius: 4,
                        padding: "4px 8px",
                        zIndex: 1,
                      }}
                      aria-label="Entrega Express disponible"
                    >
                      Express
                    </span>
                  )}
                  <ProductImage
                    src={product.images?.[0]?.small || ""}
                    alt={product.name}
                    className={styles.productImage}
                    width={160}
                    height={160}
                    loading="lazy"
                  />
                </div>
                {/* Button directly below image, left-justified */}
                <button
                  className={styles.actionButton}
                  style={{
                    marginTop: 8,
                    marginBottom: 8,
                    alignSelf: "flex-start",
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    if (
                      product.hasChildren ||
                      product.type === "configurable"
                    ) {
                      window.location.href = `/p/${product.url}`;
                    } else {
                      window.location.href = `/p/${product.url}`;
                    }
                  }}
                  aria-label={
                    product.hasChildren || product.type === "configurable"
                      ? `Ver opciones de ${product.name}`
                      : `Añadir ${product.name} al carrito`
                  }
                  tabIndex={-1}
                  type="button"
                >
                  {product.hasChildren || product.type === "configurable" ? (
                    "Opciones"
                  ) : (
                    <>
                      <AddIcon
                        className="icon"
                        aria-hidden="true"
                        style={{ fontSize: 18, marginRight: 4 }}
                      />
                      Añadir
                    </>
                  )}
                </button>
                <div className={styles.productPriceContainer}>
                  <p
                    className={`${styles.centralPrice} ${
                      oldPrice ? styles.discountedPrice : styles.regularPrice
                    }`}
                  >
                    {centralPrice}
                  </p>
                  {oldPrice && (
                    <p
                      className={styles.oldPrice}
                      aria-label={`Precio anterior: ${oldPrice}`}
                    >
                      <s>{oldPrice}</s>
                    </p>
                  )}
                </div>
                {priceRange && (
                  <p className={styles.priceRange}>{priceRange}</p>
                )}
                {product.fastestDeliveryDate && (
                  <p className={styles.fastestDeliveryDate}>
                    {product.fastestDeliveryDate}
                  </p>
                )}
                <div className={styles.productName}>{product.name}</div>
                {colors.length > 0 && (
                  <div
                    className={styles.colorOptions}
                    aria-label={`${colors.length} colores disponibles`}
                  >
                    {colors.map((color: string, idx: number) => (
                      <span
                        key={idx}
                        className={styles.colorCircle}
                        style={{
                          backgroundColor: color,
                          border:
                            color?.toLowerCase() === "#fff" ||
                            color?.toLowerCase() === "#ffffff"
                              ? "0.5px solid #ccc"
                              : "0.5px solid #fff",
                        }}
                        aria-hidden="true"
                      />
                    ))}
                  </div>
                )}
              </a>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default RecommendedProducts;
